/**
 * Check if an axios error is a 403 usage limit error.
 */
export function isUsageLimitError(error) {
  return (
    error?.response?.status === 403 &&
    error?.response?.data?.usage_type != null
  );
}

/**
 * Build a friendly message for a usage limit error.
 * @param {object} error - Axios error object
 * @returns {string} User-friendly message
 */
export function getUsageLimitMessage(error) {
  const data = error?.response?.data;
  if (!data) return null;

  const usageType = data.usage_type;
  const used = data.used;
  const limit = data.limit;

  // Map usage_type to friendly tool names
  const toolNames = {
    screener_contracts: 'Screener',
    watchlist_contracts: 'Watchlist',
    saved_parameters: 'Saved Parameters',
  };
  const toolName = toolNames[usageType] || 'this tool';

  // Hard cap types don't use monthly language
  if (usageType === 'saved_parameters') {
    return (
      `You've reached your limit for ${toolName}.\n\n` +
      `You have ${used} of ${limit} saved parameters.\n\n` +
      `Delete existing saved parameters or upgrade your plan for more capacity.`
    );
  }

  // Suggest the other tool
  const otherTool = usageType === 'screener_contracts' ? 'Watchlist' : 'Screener';

  return (
    `You've reached your monthly limit for the ${toolName}.\n\n` +
    `You've used ${used} of ${limit} ${toolName.toLowerCase()} contracts this month.\n\n` +
    `Try using the ${otherTool} if you still have usage available, ` +
    `or upgrade your plan for more capacity.`
  );
}

/**
 * Extract structured data from a 403 usage limit error for the UsageLimitModal.
 * @param {object} error - Axios error object
 * @returns {object|null} Structured usage limit data
 */
export function getUsageLimitData(error) {
  const data = error?.response?.data;
  if (!data) return null;

  const usageType = data.usage_type;
  const toolNames = {
    screener_contracts: 'Screener',
    watchlist_contracts: 'Watchlist',
    saved_parameters: 'Saved Parameters',
  };
  const toolRoutes = {
    screener_contracts: '/screener',
    watchlist_contracts: '/watchlist',
    saved_parameters: '/screener',
  };

  // Determine the "other tool" to suggest
  let otherType;
  if (usageType === 'saved_parameters') {
    otherType = 'watchlist_contracts';
  } else {
    otherType = usageType === 'screener_contracts' ? 'watchlist_contracts' : 'screener_contracts';
  }

  return {
    toolName: toolNames[usageType] || 'this tool',
    otherToolName: toolNames[otherType],
    otherToolRoute: toolRoutes[otherType],
    used: data.used,
    limit: data.limit,
    usageType,
  };
}

/**
 * Check if an axios error is a 403 feature access denied error.
 * These are returned when a user's subscription tier doesn't include a feature.
 */
export function isFeatureAccessError(error) {
  return (
    error?.response?.status === 403 &&
    error?.response?.data?.permission_type != null
  );
}

/**
 * Extract structured data from a 403 feature access error for the FeatureAccessModal.
 * @param {object} error - Axios error object
 * @returns {object|null} Structured feature access data
 */
export function getFeatureAccessData(error) {
  const data = error?.response?.data;
  if (!data) return null;

  const featureCode = data.feature_code;

  // Map feature codes to friendly names and alternative tool suggestions
  const otherTools = {
    screener: { name: 'Watchlist', route: '/watchlist' },
    watchlist: { name: 'Screener', route: '/' },
    dashboard_builder: { name: 'Watchlist', route: '/watchlist' },
    stock_analyzer: { name: 'Screener', route: '/' },
  };

  const other = otherTools[featureCode] || { name: 'Watchlist', route: '/watchlist' };

  return {
    featureName: data.feature_name || featureCode,
    featureCode,
    tierName: data.tier_name,
    tierDisplay: data.tier_display,
    permissionType: data.permission_type,
    detail: data.detail,
    otherToolName: other.name,
    otherToolRoute: other.route,
  };
}

/**
 * Fallback: check if an axios error is a generic 403 permission denied
 * (for cases where the backend hasn't been updated with structured metadata yet).
 */
export function isGenericPermissionError(error) {
  return (
    error?.response?.status === 403 &&
    error?.response?.data?.detail != null &&
    error?.response?.data?.usage_type == null &&
    error?.response?.data?.permission_type == null
  );
}
